import axios from "../../config/axios";
import { useToast } from 'vue-toastification';
import { loginWithGoogle, parseGoogleOAuthCallback, isRestrictedWebView } from '@/utils/oauth';
import { clearTrendingCache } from '@/utils/trendingCache';

const toast = useToast();

export default {
  name: 'Login',
  data() {
    return {
      username: '', // Username input field
      password: '',
      showPassword: false,
      isLoading: false,
      isOtpStep: false,
      otpCode: '',
      isOtpLoading: false,
      isResendingOtp: false,
      accountId: null,
      otpCountdown: 0,
      countdownTimer: null,
      isGoogleLoading: false,
      errors: {
        username: '',
        password: '',
        otp: ''
      },
      quoteIndex: 0,
      quotes: [
        { text: "Một cuốn sách hay trên giá là một người bạn dù quay lưng lại vẫn là người bạn tốt.", author: "Khuyết danh" },
        { text: "Sách là nguồn tài nguyên vô tận, đọc sách giúp chúng ta khám phá những chân trời mới.", author: "Victor Hugo" },
        { text: "Việc đọc tất cả những cuốn sách hay cũng như trò chuyện với những bộ óc tuyệt vời nhất của các thế kỷ đã qua.", author: "René Descartes" }
      ],
      quoteTimer: null
    };
  },
  computed: {
    currentQuote() {
      return this.quotes[this.quoteIndex];
    },
    formattedCountdown() {
      const minutes = Math.floor(this.otpCountdown / 60);
      const seconds = this.otpCountdown % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  },
  mounted() {
    this.startQuoteRotation();

    // ── Xử lý Google OAuth Redirect callback (dành cho Zalo / In-App WebView) ──
    // Khi Google redirect về /login#id_token=..., tự động đăng nhập
    const oauthResult = parseGoogleOAuthCallback();
    if (oauthResult && oauthResult.idToken) {
      this.processGoogleToken(oauthResult.idToken);
      return; // Không cần xử lý tiếp bên dưới
    }

    // Check if redirect message is stored
    const registerSuccessMsg = localStorage.getItem('register-success-msg');
    if (registerSuccessMsg) {
      toast.success(registerSuccessMsg);
      localStorage.removeItem('register-success-msg');
    }
  },
  beforeUnmount() {
    if (this.quoteTimer) {
      clearInterval(this.quoteTimer);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    clearError(field) {
      this.errors[field] = '';
    },
    validateForm() {
      let isValid = true;

      // Username check
      if (!this.username) {
        this.errors.username = "Vui lòng nhập tên đăng nhập";
        isValid = false;
      }

      // Password check
      if (!this.password) {
        this.errors.password = "Vui lòng nhập mật khẩu";
        isValid = false;
      } else if (this.password.length < 6) {
        this.errors.password = "Mật khẩu phải có tối thiểu 6 ký tự";
        isValid = false;
      }

      return isValid;
    },
    handleLogin() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;

      axios
        .post("account/v1/login", {
          username: this.username,
          password: this.password,
        })
        .then((response) => {
          if (response.data.status === '500') {
            this.isLoading = false;
            toast.error(response.data.message);
          } else if (response.data.status === '200') {
            this.isLoading = false;
            if (response.data.message === 'OTP_REQUIRED') {
              this.accountId = response.data.result;
              this.isOtpStep = true;
              this.startOtpCountdown();
              toast.info('Mã OTP đã được gửi đến email của bạn!');
            } else {
              this.onLoginSuccess(response.data.result, this.username);
            }
          } else {
            this.isLoading = false;
            toast.error(response.data.message);
          }
        })
        .catch((error) => {
          this.isLoading = false;
        });
    },
    onLoginSuccess(result, defaultUsername) {
      this.username = "";
      this.password = "";
      this.otpCode = "";
      this.isOtpStep = false;

      const fullName = result.fullName || result.fullname || "";
      const username = result.username || defaultUsername;

      // Clear trending cache to force fresh trending data fetch for new user
      clearTrendingCache();

      // Save user profile details
      localStorage.setItem(
        "current-account",
        JSON.stringify({
          id: result.id,
          username: username,
          authority: result.authority,
          name: fullName,
          fullName: fullName
        })
      );

      const token = result.token;
      localStorage.setItem("token", token);
      if (result.refreshToken) {
        localStorage.setItem("refreshToken", result.refreshToken);
      }

      axios.defaults.headers['Authorization'] = `Bearer ${token}`;

      // Thông báo cho Home xóa cache đề xuất cũ và gọi API mới
      window.dispatchEvent(new CustomEvent('user-logged-in'));

      this.silentFetchAvatar();

      this.$router.push('/');
    },
    silentFetchAvatar() {
      // Silent background fetch for user avatar - không hiển thị lỗi, nếu lỗi dùng avatar mặc định
      axios.post("account/get-avatar")
        .then(res => {
          if (res.data && (res.data.status === '200' || res.data.status === '0') && res.data.result && res.data.result !== 'NO_AVATAR') {
            let avatar = res.data.result;
            if (avatar && !avatar.startsWith('data:') && !avatar.startsWith('http')) {
              avatar = 'data:image/png;base64,' + avatar;
            }
            localStorage.setItem("userAvatar", avatar);
            window.dispatchEvent(new CustomEvent('avatar-updated', { detail: avatar }));
          } else {
            this.useDefaultAvatar();
          }
        })
        .catch(() => {
          this.useDefaultAvatar();
        });
    },
    useDefaultAvatar() {
      localStorage.removeItem("userAvatar");
      window.dispatchEvent(new CustomEvent('avatar-updated', { detail: '' }));
    },
    startOtpCountdown() {
      this.otpCountdown = 180;
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      this.countdownTimer = setInterval(() => {
        if (this.otpCountdown > 0) {
          this.otpCountdown--;
        } else {
          clearInterval(this.countdownTimer);
        }
      }, 1000);
    },
    handleResendOtp() {
      if (this.isResendingOtp || !this.accountId) return;

      this.isResendingOtp = true;
      axios.post("account/v1/resend-otp", {
        accountId: this.accountId
      })
        .then(response => {
          this.isResendingOtp = false;
          if (response.data.status === '200') {
            toast.success("Mã OTP mới đã được gửi!");
            this.startOtpCountdown();
          } else {
            toast.error(response.data.message || "Lỗi gửi lại mã OTP");
          }
        })
        .catch(error => {
          this.isResendingOtp = false;
          toast.error(error.response?.data?.message || "Lỗi hệ thống khi gửi lại mã");
        });
    },
    handleVerifyOtp() {
      if (!this.otpCode || this.otpCode.length < 6) {
        this.errors.otp = 'Mã OTP không hợp lệ';
        return;
      }
      this.isOtpLoading = true;

      axios.post("account/v1/verify-login-otp", {
        accountId: this.accountId,
        otp: this.otpCode
      })
        .then((response) => {
          this.isOtpLoading = false;
          if (response.data.status === '200') {
            toast.success(response.data.message || 'Xác thực OTP thành công!');
            this.onLoginSuccess(response.data.result, this.username);
          } else {
            toast.error(response.data.message || 'Mã OTP không chính xác');
          }
        })
        .catch((error) => {
          this.isOtpLoading = false;
          toast.error("Có lỗi xảy ra trong quá trình xác thực OTP.");
          console.error("OTP verification error:", error);
        });
    },
    async handleGoogleLogin() {
      this.isGoogleLoading = true;
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;

      // Với Zalo / In-App WebView: loginWithGoogle() sẽ redirect trang ngay lập tức
      // (không trả về Promise), nên ta set isGoogleLoading = true trước rồi để tự redirect
      const redirectUri = window.location.origin + '/login';

      try {
        const idToken = await loginWithGoogle(clientId, redirectUri);

        // Nếu là WebView redirect thì loginWithGoogle redirect trang, không có idToken ở đây
        if (!idToken) return;

        await this.processGoogleToken(idToken);
      } catch (error) {
        console.error("Google login error:", error);
        if (error.message && (error.message.includes("hủy") || error.message.includes("cancel"))) {
          console.log("Google login canceled by user.");
        } else {
          toast.error(error.message || "Không thể đăng nhập bằng Google.");
        }
        this.isGoogleLoading = false;
      }
    },

    /**
     * Gửi idToken lên backend và xử lý kết quả đăng nhập Google.
     * Dùng chung cho cả flow SDK (popup) lẫn flow Redirect (Zalo WebView).
     */
    async processGoogleToken(idToken) {
      this.isGoogleLoading = true;
      try {
        const response = await axios.post("account/v1/google", {
          idToken: idToken
        });

        if (response.data && response.data.status === '200' && response.data.result) {
          const result = response.data.result;
          const fullName = result.fullName || result.fullname || "";
          const username = result.username || "";

          clearTrendingCache();
          localStorage.setItem(
            "current-account",
            JSON.stringify({
              id: result.id,
              username: username,
              authority: result.authority,
              name: fullName,
              fullName: fullName
            })
          );

          const token = result.token;
          localStorage.setItem("token", token);
          if (result.refreshToken) {
            localStorage.setItem("refreshToken", result.refreshToken);
          }

          axios.defaults.headers['Authorization'] = `Bearer ${token}`;

          // Thông báo cho Home xóa cache đề xuất cũ và gọi API mới
          window.dispatchEvent(new CustomEvent('user-logged-in'));

          this.silentFetchAvatar();

          toast.success("Đăng nhập bằng Google thành công!");
          this.$router.push('/');
        } else {
          toast.error(response.data?.message || "Đăng nhập Google thất bại, vui lòng thử lại.");
        }
      } catch (error) {
        console.error("Google processToken error:", error);
        toast.error(error.message || "Không thể đăng nhập bằng Google.");
      } finally {
        this.isGoogleLoading = false;
      }
    },
    startQuoteRotation() {
      this.quoteTimer = setInterval(() => {
        this.quoteIndex = (this.quoteIndex + 1) % this.quotes.length;
      }, 6000);
    }
  }
};
